/**
 * Dashboard overview — KPIs, subscale radar, site ranking table, recent obs.
 */

import { useDashboardContext } from '@/components/layout/DashboardShell';
import { KpiCardRow } from '@/components/dashboard/KpiCardRow';
import { SystemRadarChart } from '@/components/dashboard/SystemRadarChart';
import { SiteRankingTable } from '@/components/dashboard/SiteRankingTable';
import { computeKpis, computeRadarData, computeSiteRankings } from '@/lib/dashboard/aggregations';

export function OverviewPage() {
  const { filtered } = useDashboardContext();
  const kpis = computeKpis(filtered);
  const radarData = computeRadarData(filtered);
  const rankings = computeSiteRankings(filtered);

  // Recent 5 observations
  const recent = [...filtered]
    .sort((a, b) => b.exportedAt.localeCompare(a.exportedAt))
    .slice(0, 5);

  if (filtered.length === 0) {
    return (
      <div className="mx-auto flex max-w-7xl flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <p className="text-lg font-medium text-foreground">No observations yet</p>
        <p className="text-sm text-muted-foreground">
          Submit observations from the FCCERS-3 tool to see dashboard analytics here.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-6">
      <KpiCardRow data={kpis} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SystemRadarChart data={radarData} />
        <SiteRankingTable rankings={rankings} />
      </div>

      {/* Recent observations mini-table */}
      <div className="rounded-xl border bg-card p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Recent Observations
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Site</th>
                <th className="px-4 py-2 font-medium">Observer</th>
                <th className="px-4 py-2 font-medium text-center">Avg</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.recordId} className="border-b last:border-0">
                  <td className="px-4 py-2">{o.observationDate}</td>
                  <td className="px-4 py-2 font-medium">{o.siteName}</td>
                  <td className="px-4 py-2">{o.observerName}</td>
                  <td className="px-4 py-2 text-center">
                    {o.totalAverage !== null ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.totalAverage >= 5 ? 'bg-green-50 text-green-700' : o.totalAverage >= 3 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                        {o.totalAverage.toFixed(2)}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
